import React, { useRef, useMemo, useState, useEffect } from "react";
import { Alert } from "services/Alert";

import { toStartDayUTCTimeNextDay, toStartDayUTCTime, compareObjects } from "utils";
import { useWindowWidth } from "hooks";
import { api } from "api/loginRoutes";
import {
    ACCOUNT_SERVICE_PLAN_LIST_API,
    SMS_SUPPLIER_TRUNK_CREATE_METHOD,
    SMS_SUPPLIER_TRUNK_MODIFY_METHOD,
    SMS_SUPPLIER_TRUNK_IP_CREATE_METHOD,
    SMS_SUPPLIER_TRUNK_IP_MODIFY_METHOD,
    SMS_SUPPLIER_TRUNK_IP_REMOVE_METHOD,
} from "const/apiMethods";
import Modal from "components/Modal";
import TrunkForm from "../TrunkForm/TrunkFormRefactored";
import { buildInitTrafficLimitList, hasAnyOrganic } from "./TrunkModal.utils";


export default (props) => {
    const {
        onClose,
        trunksList,
        account_id,
        initialValues,
        ipOptionList,
        update,
        account,
        account_name,
        isGanTrunks,
        currentSupplier,
        isTransitTrunks,
        paymentTermsList,
        supplierPriceList,
        rerouteStatusList,
        transitDefaultLimit,
        accountServicePlanList,
        getSupplierPriceList,
        createTrafficLimit,
        modifyTrafficLimit,
        removeTrafficLimit,
    } = props;

    const {
        initTrafficLimitList,
        defaultDayValue,
        defaultHourValue,
    } = buildInitTrafficLimitList({ initialValues, isTransitTrunks, transitDefaultLimit });

    const formTrafficLimitRef = initTrafficLimitList.reduce((result, trafficLimit) => {
        result[trafficLimit.key] = useRef(null);
        return result;
    }, {});

    const form = useRef(null);
    const nonDialerTrafficRunForm = useRef(null);

    const windowWidth = useWindowWidth();
    const [disabled, setDisabled] = useState(false);
    const [error, setError] = useState({});
    const [protocolType, setProtocolType] = useState(initialValues?.message_protocol || "smpp");
    const [modalWarningShow, setModalWarningShow] = useState(false);
    const [moreData, setMoreData] = useState({
        payout_model: initialValues?.payout_model,
        revenue_percent: initialValues?.revenue_percent,
        postback_url: initialValues?.postback_url
    });

    const [termPoint, setTermPoint] = useState(initialValues.termination_point);

    const [ipList, setIpList] = useState(initialValues && initialValues?.ip_list && JSON.parse(JSON.stringify(initialValues?.ip_list)) || []);
    const [httpIpList, setHttpIpList] = useState(initialValues && initialValues?.ip_white_list && JSON.parse(JSON.stringify(initialValues?.ip_white_list)) || []);
    const [moreOptionsFlag, setMoreOptionsFlag] = useState(!!initialValues?.payout_model || !!initialValues?.postback_url);
    const [trafficLimitKeyList, setTrafficLimitKeyList] = useState(initTrafficLimitList);
    const [nonDialerPartial, setNonDialerPartial] = useState(false);
    const [nonDialerValid, setNonDialerValid] = useState(true);

    const protocolTypeValues = useMemo(() => {
        return {
            http: protocolType === "http/https",
            smpp: protocolType === "smpp",
            smpp_mo: protocolType === "smpp_mo"
        };
    }, [protocolType]);

    const emptyValueList = useMemo(() => {
        return (protocolTypeValues.smpp || protocolTypeValues.smpp_mo)
            ? ipList.filter((row) => (!row.ip))
            : httpIpList.filter((ip) => (!ip));
    }, [protocolType, ipList, httpIpList, protocolTypeValues]);

    useEffect(() => {
        if (getSupplierPriceList) {
            getSupplierPriceList(currentSupplier.supplier_id);
        }
    }, [currentSupplier, getSupplierPriceList]);

    const onSubmit = async () => {
        const currentTrafficLimitKeyList = trafficLimitKeyList.map((x) => x);

        if (!form.current.check()) return;

        if (isTransitTrunks && nonDialerPartial) {
            if (!nonDialerValid) {
                Alert.error("Fill all Non-dialer fields correctly", 5000);
                return;
            }
        }

        trafficLimitKeyList.forEach((trafficLimit, idx) => {
            const hasNotData = isTransitTrunks ? !trafficLimit?.day : !trafficLimit?.hour || !trafficLimit?.day;

            if (trafficLimit.key.toString().startsWith("new") && hasNotData) {
                currentTrafficLimitKeyList.splice(idx, 1);
                setTrafficLimitKeyList(currentTrafficLimitKeyList);
            }
        });

        if (disabled) return;

        setDisabled(true);
        const method = initialValues.id ? SMS_SUPPLIER_TRUNK_MODIFY_METHOD : SMS_SUPPLIER_TRUNK_CREATE_METHOD;

        let _data = form.current.getFormValue();
        const todayDate = toStartDayUTCTime(new Date());
        const startDate = _data.start_date ? toStartDayUTCTime(new Date(_data.start_date)) : null;
        const isToday = startDate && (
            todayDate.getFullYear() === startDate.getFullYear() &&
            todayDate.getMonth() === startDate.getMonth() &&
            todayDate.getDate() === startDate.getDate()
        );

        const discountList = isGanTrunks ? paymentTermsList.map(paymentTerm => {
            const paymentTermKey = `discount_${paymentTerm.name}`;
            if (Object.keys(_data).includes(paymentTermKey)) {
                const discount = _data[paymentTermKey];
                return { pt_key: paymentTerm.pt_key, discount: parseFloat(discount / 100) };
            }
            return null;
        }).filter(item => !!item) : [];

        const data = {
            name: _data.name,
            sp_key: _data.sp_key,
            prt_key: _data.prt_key || null,
            rrc_key: _data.rrc_key,
            start_date: !initialValues.id
                ? (startDate && !isToday) ? startDate : null
                : isToday
                    ? _data.start_date ? _data.start_date : null
                    : startDate,
            end_date: _data.end_date
                ? toStartDayUTCTimeNextDay(new Date(_data.end_date))
                : null,
            active: !!_data.active,
            tariffication_all: !!_data.tariffication_all,
            ...(isTransitTrunks && !initialValues.id ? { custom_ranges: _data.custom_ranges } : {}),
            termination_point: _data.termination_point,
            smpp_reroute_status_key: _data.smpp_reroute_status_key,
            delivery_delay_type: _data.delivery_delay_type,
            dlr_percent: _data.dlr_percent / 100,
            bill_only_delivered: _data.bill_only_delivered,
            block_traffic_limit_excess: _data.block_traffic_limit_excess,
            ...(!protocolTypeValues.http ? {
                systemid: _data.systemid,
                passwd: _data.passwd
            } : {}),
            ...(_data?.comment_admin ? { comment_admin: _data.comment_admin } : {}),
            ...(_data?.comment_dialer ? { comment_dialer: _data.comment_dialer } : {}),
            ...(discountList.length ? { discount_list: discountList } : {})
        };

        if (isTransitTrunks && nonDialerTrafficRunForm.current) {
            const formData = nonDialerTrafficRunForm.current.getFormValue();
            data.organic_login = formData.organic_login || undefined;
            data.organic_password = formData.organic_password || undefined;
            data.organic_ip = formData.organic_ip || undefined;
            data.organic_port = parseInt(formData.organic_port) || undefined;
            data.organic_use_ssl = formData.organic_use_ssl || false;
            data.pass_all_traffic_to_organic = formData.pass_all_traffic_to_organic || false;
        }

        if (httpIpList.length) {
            data.ip_white_list = httpIpList;
        }

        const optionalFormData = moreData;
        if (optionalFormData.postback_response_mime_type) data.postback_response_mime_type = optionalFormData.postback_response_mime_type;
        if (optionalFormData.postback_response_body) data.postback_response_body = optionalFormData.postback_response_body;

        data.message_protocol = protocolType;

        if (moreOptionsFlag) {
            if (optionalFormData.postback_url || optionalFormData.postback_url === "") data.postback_url = optionalFormData.postback_url;
            if (optionalFormData.postback_method) data.postback_method = optionalFormData.postback_method;
            if (optionalFormData.payout_model) data.payout_model = parseInt(optionalFormData.payout_model);
            if (optionalFormData.revenue_percent) data.revenue_percent = parseFloat(parseFloat(optionalFormData.revenue_percent).toFixed(1));
        }

        data.ip_list = initialValues?.ip_list;

        if (isGanTrunks) data.type = "gan";
        else if (isTransitTrunks) data.type = "transit";

        const currentSpPlanList = await api(ACCOUNT_SERVICE_PLAN_LIST_API, { target: { account_id: account_id } })
            .then(({ account_service_plan_list }) => account_service_plan_list);
        const currentSpPlan = currentSpPlanList.find(item => item.sp_key === data.sp_key);

        if ((currentSpPlan && !currentSpPlan.allowed) && data.active) {
            setModalWarningShow(true);
            setDisabled(false);
            return;
        }

        let isChanged = false;

        try {
            await api(method, {
                target: {
                    account_id,
                    "sms.supplier_id": currentSupplier.supplier_id,
                    "sms.supplier_trunk_id": _data.id,
                    ...(_data?.supplier_price_id ? { "sms.supplier_price_id": _data.supplier_price_id } : {}),
                    ...(Object.keys(_data).includes("ivr_id") ? { ivr_id: _data.ivr_id } : {})
                },
                ...data,
                sp_key: data.sp_key
            }).then(async (response) => {
                if (!response["sms.supplier_trunk"]) return;

                const initIpList = initialValues?.ip_list || [];
                const supplierTrunkId = initialValues.id ? _data.id : response["sms.supplier_trunk"];

                ipList.forEach(async (ipValue) => {
                    const initIpValue = initIpList.find(ip => ip.key === ipValue.key);
                    const optionBitMask = ipValue?.options ? ipValue.options.reduce((acc, value) => acc += value, 0) : 0;

                    if (!ipValue.key) {
                        isChanged = true;
                        await api(SMS_SUPPLIER_TRUNK_IP_CREATE_METHOD, {
                            target: { "sms.supplier_trunk_id": supplierTrunkId },
                            "sms.supplier_trunk_ip_key": ipValue.key,
                            active: true,
                            ...ipValue,
                            ...(ipValue?.port ? { port: parseInt(ipValue.port) } : {}),
                            options: optionBitMask
                        }).finally(() => update());
                    } else if (!compareObjects(initIpValue, ipValue)) {
                        isChanged = true;
                        await api(SMS_SUPPLIER_TRUNK_IP_MODIFY_METHOD, {
                            target: { "sms.supplier_trunk_id": supplierTrunkId },
                            "sms.supplier_trunk_ip_key": ipValue.key,
                            ...ipValue,
                            ...(ipValue?.port ? { port: parseInt(ipValue.port) } : {}),
                            options: optionBitMask
                        }).finally(() => update());
                    }
                });

                initIpList.forEach(async (initIpValue) => {
                    const deletedIpValue = !ipList.find(ipValue => ipValue.key == initIpValue.key);
                    if (deletedIpValue) {
                        isChanged = true;
                        await api(SMS_SUPPLIER_TRUNK_IP_REMOVE_METHOD, {
                            target: { "sms.supplier_trunk_id": supplierTrunkId },
                            "sms.supplier_trunk_ip_key": initIpValue.key
                        }).finally(() => update());
                    }
                });

                const initTrafficLimit = initialValues?.trafic_limit_list || [];

                if (currentTrafficLimitKeyList.length) {
                    currentTrafficLimitKeyList.forEach(async (trafficLimit) => {
                        const currentInitialValue = initTrafficLimit.find(initTrafficLimit => initTrafficLimit.key === trafficLimit.key);

                        if (trafficLimit.key.toString().startsWith("new")) {
                            isChanged = true;
                            await createTrafficLimit({
                                target: { "sms.supplier_trunk_id": supplierTrunkId },
                                ...trafficLimit,
                                ...(isTransitTrunks ? {} : { hour: parseInt(trafficLimit.hour) }),
                                day: parseInt(trafficLimit.day),
                                type: "trunk"
                            }).finally(() => update(true));
                        } else if (!compareObjects(currentInitialValue, trafficLimit)) {
                            isChanged = true;
                            await modifyTrafficLimit({
                                target: {
                                    "sms.supplier_id": currentSupplier?.supplier_id,
                                    "sms.traffic_limit_key_list": [trafficLimit.key]
                                },
                                ...(isTransitTrunks ? {} : { hour: parseInt(trafficLimit.hour) }),
                                day: parseInt(trafficLimit.day),
                                type: "trunk"
                            }).finally(() => update(true));
                        }
                    });
                } else if (!initialValues.id) {
                    await createTrafficLimit({
                        target: { "sms.supplier_trunk_id": supplierTrunkId },
                        senderid: null,
                        ...defaultDayValue,
                        ...defaultHourValue,
                        type: "trunk"
                    }).finally(() => update(true));
                }

                initTrafficLimit.forEach(async (initTrafficLimitRow) => {
                    const deletedInitTrafficLimit = !currentTrafficLimitKeyList.find(trafficLimit => trafficLimit.key == initTrafficLimitRow.key);
                    if (deletedInitTrafficLimit) {
                        await removeTrafficLimit({
                            target: {
                                "sms.supplier_id": currentSupplier?.supplier_id,
                                "sms.traffic_limit_key_list": [initTrafficLimitRow.key]
                            },
                            type: "trunk"
                        }).finally(() => update(true));
                    }
                });
            }).finally(() => {
                onClose();
                if (!isChanged) {
                    update();
                }
            });
        } catch (e) {
            console.log("Change trunk error");
        }

        setDisabled(false);
    };

    const modalWidth = 1420;
    const isEmpty = emptyValueList.length;

    const isFormHasError = useMemo(() => {
        return Object.keys(error).some((formKey) => {
            const formError = error[formKey];

            return Object.keys(formError).some((itemKey) => {
                if (Array.isArray(formError[itemKey])) {
                    return formError[itemKey].length;
                }
                return !!formError[itemKey];
            });
        });
    }, [error]);

    const nonDialerGuard = isTransitTrunks && (nonDialerPartial || !nonDialerValid);

    return (
        <>
            <Modal
                onClose={onClose}
                show={true}
                onSuccess={onSubmit}
                disabled={disabled}
                extraDisabled={isFormHasError || !!isEmpty || nonDialerGuard}
                title="Create/Edit Trunk"
                footer
                successText="Save"
                width={windowWidth >= modalWidth ? modalWidth : windowWidth}
                customMarginTop={3.5}
                maxHeight={750}
            >
                <TrunkForm
                    accountServicePlanList={accountServicePlanList}
                    account={account}
                    disabled={disabled}
                    termPoint={termPoint}
                    setDisabled={setDisabled}
                    updateFormRef={form}
                    updateFormRef4={nonDialerTrafficRunForm}
                    setNonDialerPartial={setNonDialerPartial}
                    setNonDialerValid={setNonDialerValid}
                    setMoreData={setMoreData}
                    initialValues={initialValues}
                    newTrunk={!initialValues.id}
                    isMobile={windowWidth < 620}
                    error={error}
                    setError={setError}
                    ipList={ipList}
                    setIpList={setIpList}
                    httpIpList={httpIpList}
                    ipOptionList={ipOptionList}
                    setHttpIpList={setHttpIpList}
                    moreOptionsFlag={moreOptionsFlag}
                    setMoreOptionsFlag={setMoreOptionsFlag}
                    supplierPriceList={supplierPriceList}
                    protocolType={protocolType}
                    setProtocolType={setProtocolType}
                    isGanTrunks={isGanTrunks}
                    isTransitTrunks={isTransitTrunks}
                    paymentTermsList={paymentTermsList}
                    formTrafficLimitRef={formTrafficLimitRef}
                    trafficLimitKeyList={trafficLimitKeyList}
                    setTrafficLimitKeyList={setTrafficLimitKeyList}
                    rerouteStatusList={rerouteStatusList}
                    initTrafficLimitList={initTrafficLimitList}
                    protocolTypeValues={protocolTypeValues}
                    hasAnyOrganic={hasAnyOrganic}
                />
            </Modal>
            {modalWarningShow && <Modal
                show={modalWarningShow}
                onClose={() => setModalWarningShow(false)}
                title="Submit is denied"
            >
                <p>This Price is not allowed for dialer</p>
            </Modal>}
        </>
    );
};
